import React, { useEffect, useMemo } from 'react';
import { FileText } from 'lucide-react';

/**
 * Right-column "Live Stage" — shows a real-time preview of the uploaded
 * artwork with the chosen cutline shape overlaid as a dashed magenta
 * die-line.
 *
 * For raster images (PNG / JPG / WEBP / GIF / SVG) we render via <img>.
 * For PDFs we use the browser's native viewer inside an <iframe>.
 * For vector design files (AI / EPS / PSD) we show a file-type badge
 * since no browser renders those natively.
 *
 * Props:
 *  - file: File | null  first uploaded artwork
 *  - cutlineShape: string | ''   rectangle | circle | rounded-rectangle | custom | ''
 *  - width / height: number  the ordered dimensions (for aspect hint)
 */
const LivePreviewStage = ({ file, cutlineShape, width, height }) => {
  const kind = detectPreviewKind(file);

  // Single Object URL creation keyed off file+kind so the PDF iframe and
  // the <img> tag share one source. `null` for unrenderable files.
  const previewUrl = useMemo(
    () => (file && kind !== 'badge' ? URL.createObjectURL(file) : null),
    [file, kind]
  );

  // Revoke the object URL when the file changes or component unmounts to
  // avoid memory leaks on files > 5MB.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Choose the aspect ratio of the overlay box from the ordered size so
  // circle/rounded-rect cutlines are drawn truthfully (not always square).
  const ordW = Number(width) || 1;
  const ordH = Number(height) || 1;
  const boxAspect = `${ordW} / ${ordH}`;

  return (
    <div
      data-testid="live-preview-canvas"
      className="relative w-full aspect-square md:aspect-[4/3] bg-[#F4F5F0] rounded-3xl border border-slate-200 overflow-hidden shadow-inner flex items-center justify-center"
      style={CANVAS_BG_STYLE}
    >
      {!file ? (
        <EmptyStatePlaceholder />
      ) : (
        <div
          className="relative flex items-center justify-center"
          style={{ width: '72%', aspectRatio: boxAspect }}
        >
          <PreviewBody kind={kind} file={file} previewUrl={previewUrl} />
          {cutlineShape && <CutlineOverlay shape={cutlineShape} />}
          {cutlineShape === 'custom' && file && <CustomDieLineHint />}
        </div>
      )}
      {cutlineShape && (
        <span
          className="absolute top-3 left-3 bg-white/90 px-2 py-1 rounded-md text-[10px] uppercase tracking-widest text-[#2F5D3A] font-semibold shadow-sm"
          data-testid="live-preview-cutline-label"
        >
          Die-line: {cutlineShape.replace('-', ' ')}
        </span>
      )}
    </div>
  );
};

// ---- Helpers (module scope keeps reference equality across renders) ----

const CANVAS_BG_STYLE = {
  backgroundImage:
    'linear-gradient(to right, rgba(47,93,58,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(47,93,58,0.08) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
};

/** Classify a file into one of three render strategies. */
function detectPreviewKind(file) {
  if (!file) return 'none';
  const type = file.type || '';
  const name = file.name || '';
  if (/^image\/(png|jpe?g|webp|gif|svg\+xml)$/i.test(type)) return 'img';
  if (/^application\/pdf$/i.test(type) || /\.pdf$/i.test(name)) return 'pdf';
  return 'badge';
}

const EmptyStatePlaceholder = () => (
  <div className="text-center px-6">
    <svg viewBox="0 0 120 120" className="w-24 h-24 mx-auto mb-4 opacity-50">
      <rect x="10" y="15" width="100" height="75" rx="8" fill="none" stroke="#2F5D3A" strokeWidth="2" strokeDasharray="6 4" />
      <circle cx="38" cy="42" r="6" fill="#2F5D3A" opacity="0.5" />
      <polyline points="15,78 45,52 70,62 105,38" fill="none" stroke="#2F5D3A" strokeWidth="2" opacity="0.5" />
    </svg>
    <p className="text-sm text-[#2F5D3A] font-medium">
      Your die-line preview will appear here.
    </p>
    <p className="text-xs text-gray-500 mt-1">
      Drop an artwork file on the left to get started.
    </p>
  </div>
);

/** Flattened preview body — replaces a 3-level nested ternary. */
const PreviewBody = ({ kind, file, previewUrl }) => {
  if (kind === 'img') {
    return (
      <img
        src={previewUrl}
        alt="Uploaded artwork"
        className="absolute inset-0 w-full h-full object-contain"
        data-testid="live-preview-image"
      />
    );
  }
  if (kind === 'pdf') {
    return (
      // Native browser PDF rendering via iframe — uses Chrome/Safari/
      // Firefox/Edge's built-in PDF viewer, no external library, instant
      // render. pointer-events: none keeps the die-line overlay clickable.
      <iframe
        src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        title="Artwork PDF preview"
        className="absolute inset-0 w-full h-full bg-white rounded-sm pointer-events-none border-0"
        data-testid="live-preview-pdf"
      />
    );
  }
  // kind === 'badge' — AI / EPS / PSD / unknown
  const fileExt = file?.name?.split('.').pop()?.toUpperCase() || 'FILE';
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/80 rounded-lg"
      data-testid="live-preview-file-badge"
    >
      <FileText className="w-10 h-10 text-[#4A7C59]" />
      <span className="text-[11px] uppercase tracking-widest font-bold text-[#2F5D3A]">
        {fileExt} file
      </span>
      <span className="text-[11px] text-gray-600 max-w-[85%] truncate text-center px-2" title={file?.name}>
        {file?.name || 'artwork'}
      </span>
      <span className="text-[10px] text-gray-400">
        Preview available after upload
      </span>
    </div>
  );
};

/**
 * Cutline overlay hint. For rectangle/rounded/circle we know the exact
 * final shape so we draw a magenta dashed outline. For `custom` the real
 * shape is computed server-side from the uploaded artwork's contour — we
 * can't reproduce that in the browser without downloading + running the
 * same OpenCV passes, so we deliberately do NOT draw a fake blob. Instead
 * we render a small corner badge explaining the die-line will appear on
 * the proof. This is more honest than the previous hardcoded wavy curve
 * which made customers think the blob was their actual cut path.
 */
const CutlineOverlay = ({ shape }) => (
  <svg
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    className="absolute inset-0 w-full h-full pointer-events-none"
    data-testid={`live-preview-cutline-${shape}`}
  >
    {shape === 'rectangle' && (
      <rect x="3" y="3" width="94" height="94"
        fill="none" stroke="#E91E63" strokeWidth="1.2" strokeDasharray="2 1.5" />
    )}
    {shape === 'rounded-rectangle' && (
      <rect x="3" y="3" width="94" height="94" rx="12" ry="12"
        fill="none" stroke="#E91E63" strokeWidth="1.2" strokeDasharray="2 1.5" />
    )}
    {shape === 'circle' && (
      <ellipse cx="50" cy="50" rx="47" ry="47"
        fill="none" stroke="#E91E63" strokeWidth="1.2" strokeDasharray="2 1.5" />
    )}
    {/* shape === 'custom' intentionally renders no overlay here —
        see CustomDieLineHint rendered outside the SVG below. */}
  </svg>
);

const CustomDieLineHint = () => (
  <div
    className="absolute bottom-2 left-2 right-2 mx-auto max-w-xs bg-white/90 backdrop-blur-sm border border-[#E91E63]/30 rounded-md px-3 py-2 text-[11px] text-gray-700 flex items-start gap-2 pointer-events-none"
    data-testid="live-preview-cutline-custom-hint"
  >
    <span className="w-3 h-3 mt-[2px] rounded-sm border-2 border-dashed border-[#E91E63] flex-shrink-0" />
    <span>
      <strong className="text-[#2F5D3A]">Custom die-line</strong> — our AI traces
      your artwork's outline. You'll see the exact cut path on your proof in the next step.
    </span>
  </div>
);

export default LivePreviewStage;
